// @flow

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {touch, isValid, getFormValues} from 'redux-form';
import {
  clear,
  fetchOrderData,
  fetchProducts,
  fetchVariants,
  updateOrder,
  createAdditionalRow,
  updateAdditionalRow,
  removeAdditionalRow,
  setAdditionalProductRow,
  updateLinkedAdditionalRow,
  removeLinkedAdditionalRow,
  setNoteRow,
  updateOrderRow,
  toggleReportDialog,
  updateReportFilter,
  setLinkedAdditionalRow,
  updateExtraRow,
} from '../../actions/purchaseOrderView';
import validator from '../../helpers/validator';
import ProductRowList from '../../components/ProductRowList/ProductRowList';
import AdditionalRowList from '../../components/AdditionalRowList/AdditionalRowList';
import OrderCustomProductList from '../../components/OrderCustomProductList/OrderCustomProductList';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import ProductRowDialog from '../../components/ProductRowDialog/ProductRowDialog';
import LinkedAdditionalRowDialog from '../../components/LinkedAdditionalRowDialog/LinkedAdditionalRowDialog';
import OrderRelationLinks from '../../components/OrderRelationLinks/OrderRelationLinks';
import Navigation from '../../components/Navigation/Navigation';
import OrderRowNoteDialog from '../../components/OrderRowNoteDialog/OrderRowNoteDialog';
import PurchaseReportDialog from '../../components/PurchaseReportDialog/PurchaseReportDialog';
import ActionConfirmation from '../ActionConfirmation/ActionConfirmation';
import {CONFIRMED, CLOSED} from '../../orderStates';
import {stateLabels} from '../../orderStates';
import _ from 'lodash/fp';

const additionalRowValidation = {
  roomId: {rule: 'numeric|required', label: 'Rum'},
  product: {rule: 'required', label: 'Produkt'},
  productVariant: {rule: 'required', label: 'Variant'},
  quantity: {rule: 'numeric|min:0|required', label: 'Antal'},
};

const linkedAdditionalRowValidation = {
  quantity: {rule: 'numeric|min:0|required', label: 'Antal'},
  note: {rule: 'string', label: 'Anteckning'},
};

const noteRowValidation = {
  note: {rule: 'string', label: 'Anteckning'},
};

export type Props = {
  prevPath: string,
  order: ?any,
  rooms: any[],
  products: any[],
  rowsById: any,
  extraRowsById: any,
  allRowsByRoom: any[],
  customRows: any[],
  additionalRows: any[],
  allAdditionalRowsByRoom: any[],
  clear: typeof clear,
  error: ?any,
  fetchData: (id: ?any) => any,
  fetchVariants: typeof fetchVariants,
  isFetching: boolean,
  params: any,
  updateOrder: typeof updateOrder,
  touch: typeof touch,
  productsAreFetching: boolean,
  additionalRowFormValues: any,
  additionalRowFormIsValid: boolean,
  additionalRow: ?any,
  setAdditionalProductRow: typeof setAdditionalProductRow,
  setLinkedAdditionalRow: typeof setLinkedAdditionalRow,
  createAdditionalRow: typeof createAdditionalRow,
  updateAdditionalRow: typeof updateAdditionalRow,
  removeAdditionalRow: typeof removeAdditionalRow,
  variants: any[],
  variantsAreFetching: boolean,
  additionalRowError: ?any,
  additionalRowIsFetching: boolean,
  linkedAdditionalRow: ?any,
  linkedAdditionalRowError: ?any,
  linkedAdditionalRowIsFetching: boolean,
  linkedAdditionalRowFormValues: any,
  linkedAdditionalRowFormIsValid: boolean,
  updateLinkedAdditionalRow: typeof updateLinkedAdditionalRow,
  removeLinkedAdditionalRow: typeof removeLinkedAdditionalRow,
  noteRow: ?any,
  noteRowError: ?any,
  noteRowIsFetching: boolean,
  noteRowIsExtra: boolean,
  noteRowFormValues: any,
  noteRowFormIsValid: boolean,
  setNoteRow: typeof setNoteRow,
  updateOrderRow: typeof updateOrderRow,
  updateExtraRow: typeof updateExtraRow,
  toggleReportDialog: typeof toggleReportDialog,
  reportDialogVisibility: boolean,
  suppliers: any[],
  reportFilter: {
    includePlans: boolean,
    supplierId: ?number,
  },
  updateReportFilter: typeof updateReportFilter,
  orders: any[],
};

class OrderContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchData(this.props.params.id);
  }

  componentWillUnmount() {
    this.props.clear();
  }

  saveLinkedAdditionalRow() {
    const {
      linkedAdditionalRowFormValues,
      linkedAdditionalRowFormIsValid,
      linkedAdditionalRow,
      updateLinkedAdditionalRow,
      touch,
    } = this.props;
    const keys = _.keys(linkedAdditionalRowValidation);
    touch('linkedAdditionalRow', ...keys);
    if (!linkedAdditionalRowFormIsValid || !linkedAdditionalRow) {
      return;
    }
    const data = {
      ...linkedAdditionalRowFormValues,
      productVariantId: linkedAdditionalRowFormValues.productVariant.id,
    };

    updateLinkedAdditionalRow(linkedAdditionalRow.id, data);
  }

  saveAdditionalRow() {
    const {
      additionalRowFormValues,
      additionalRowFormIsValid,
      order,
      additionalRow,
      createAdditionalRow,
      updateAdditionalRow,
      touch,
    } = this.props;
    const keys = _.keys(additionalRowValidation);
    touch('productRow', ...keys);
    if (!additionalRowFormIsValid) {
      return;
    }
    const data = {
      ...additionalRowFormValues,
      productVariantId: additionalRowFormValues.productVariant.id,
    };

    if (additionalRow && !additionalRow.id && !!order) {
      createAdditionalRow(order.id, data);
    } else if (!!additionalRow) {
      updateAdditionalRow(additionalRow.id, data);
    }
  }

  saveNoteRow() {
    const {
      noteRowIsExtra,
      noteRowFormValues,
      noteRowFormIsValid,
      noteRow,
      updateOrderRow,
      updateExtraRow,
      touch,
    } = this.props;
    const keys = _.keys(noteRowValidation);
    touch('noteRow', ...keys);
    if (!noteRowFormIsValid || !noteRow) {
      return;
    }
    const data = _.pick(['note'], noteRowFormValues);

    if (noteRowIsExtra) {
      updateExtraRow(noteRow.id, data);
    } else {
      updateOrderRow(noteRow.id, data);
    }
  }

  render() {
    const {
      order,
      allRowsByRoom,
      rowsById,
      extraRowsById,
      additionalRows,
      customRows,
      rooms,
      additionalRow,
      allAdditionalRowsByRoom,
      additionalRowIsFetching,
      additionalRowError,
      linkedAdditionalRow,
      linkedAdditionalRowIsFetching,
      linkedAdditionalRowError,
      products,
      productsAreFetching,
      variants,
      variantsAreFetching,
      additionalRowFormValues,
      error,
      isFetching,
      fetchVariants,
      setAdditionalProductRow,
      setLinkedAdditionalRow,
      removeAdditionalRow,
      removeLinkedAdditionalRow,
      updateOrder,
      noteRow,
      noteRowError,
      noteRowIsFetching,
      noteRowFormValues,
      setNoteRow,
      toggleReportDialog,
      reportDialogVisibility,
      suppliers,
      reportFilter,
      updateReportFilter,
      orders,
      clear,
      fetchData,
      prevPath,
    } = this.props;

    return (
      <div className="relative">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Navigation
                current={order}
                list={orders}
                prevPath={prevPath}
                clear={clear}
                fetchData={fetchData} />
              <div className="content-title">
                {(order && 'Beställning') || 'Ny beställning'} {order && ` | ${stateLabels[order.state]}`}
              </div>
            </div>
          </div>
          {order &&
            <div className="column shrink horizontal-button-list">
              {order.state === CONFIRMED &&
                <ActionConfirmation
                  id="finishOrder"
                  className="success"
                  message="Vill du verkligen markera beställningen som klar?"
                  buttonText="Markera som klar"
                  action={() => updateOrder(order.id, {state: CLOSED})} />
              }
              {order.state === CLOSED &&
                <ActionConfirmation
                  id="revertOrder"
                  className="alert"
                  message="Vill du verkligen markera beställningen som oavslutad?"
                  buttonText="Markera som oavslutad"
                  action={() => updateOrder(order.id, {state: CONFIRMED})} />
              }
              <button
                type="button"
                className="button"
                onClick={() => toggleReportDialog(true)}>
                Ladda ner inköpsrapport
              </button>
            </div>
          }
        </div>
        <LoadingIndicator isLoading={isFetching || productsAreFetching} />
        <ServerError error={error} />
        {order &&
          <div>
            <div className="content-section box">
              <OrderRelationLinks
                project={order.apartment.project}
                apartment={order.apartment}
                user={order.user} />
            </div>
            <div className="content-section box">
              <div className="content-section">
                <div className="box-title">
                  Produkter
                </div>
                <ProductRowList
                  allRowsByRoom={allRowsByRoom}
                  onSelectRow={row => {setNoteRow(row, false);}}
                  onSelectExtraRow={row => {setNoteRow(row, true);}}/>
              </div>
              <div className="content-section">
                <div className="box-title">
                  Friprodukter
                </div>
                <OrderCustomProductList products={customRows} />
              </div>
              <div className="content-section">
                <div className="box-title">
                  Tilläggsprodukter
                </div>
                <AdditionalRowList
                  allRowsByRoom={allAdditionalRowsByRoom}
                  rowsById={rowsById}
                  extraRowsById={extraRowsById}
                  onSelectRow={row => setAdditionalProductRow(row)}
                  onRemoveRow={row => removeAdditionalRow(row.id)}
                  onSelectVariantRow={row => setLinkedAdditionalRow(row)}
                  onRemoveVariantRow={row => removeLinkedAdditionalRow(row.id)} />
                <button
                  onClick={() => setAdditionalProductRow({})}
                  type="button"
                  className="button small">
                  Lägg till tilläggsprodukt
                </button>
              </div>
            </div>
          </div>
        }
        {additionalRow &&
          <ProductRowDialog
            existingRows={additionalRows}
            products={products}
            variants={variants}
            activeRow={additionalRow}
            rooms={rooms}
            error={additionalRowError}
            isLoading={additionalRowIsFetching || variantsAreFetching}
            formValues={additionalRowFormValues}
            onSave={() => this.saveAdditionalRow()}
            onSelectProduct={productId => fetchVariants(productId)}
            onClose={() => setAdditionalProductRow(null)}
            onOpen={undefined}
            validate={values => validator(additionalRowValidation, values)} />
        }
        {linkedAdditionalRow &&
          <LinkedAdditionalRowDialog
            linkedAdditionalRow={linkedAdditionalRow}
            error={linkedAdditionalRowError}
            isLoading={linkedAdditionalRowIsFetching}
            onSave={() => this.saveLinkedAdditionalRow()}
            onClose={() => setLinkedAdditionalRow(null)}
            validate={values => validator(linkedAdditionalRowValidation, values)} />
        }
        {noteRow &&
          <OrderRowNoteDialog
            orderRow={noteRow}
            error={noteRowError}
            isLoading={noteRowIsFetching}
            formValues={noteRowFormValues}
            onSave={() => this.saveNoteRow()}
            onClose={() => setNoteRow(null)}
            validate={values => validator(noteRowValidation, values)} />
        }
        {reportDialogVisibility && !!order &&
          <PurchaseReportDialog
            order={order}
            suppliers={suppliers}
            filter={reportFilter}
            updateFilter={updateReportFilter}
            onClose={() => toggleReportDialog(false)} />
        }
      </div>
    );
  }
}

const mapStateToProps = ({
  purchaseOrderView: {
    active: order,
    rows: {
      rowsById,
      extraRowsById,
      allRowsByRoom,
    },
    additionalRows: {
      additionalRows,
      allRowsByRoom: allAdditionalRowsByRoom,
    },
    customRows,
    rooms,
    additionalRow: {
      active: additionalRow,
      error: additionalRowError,
      isFetching: additionalRowIsFetching,
    },
    linkedAdditionalRow: {
      active: linkedAdditionalRow,
      error: linkedAdditionalRowError,
      isFetching: linkedAdditionalRowIsFetching,
    },
    products: {
      isFetching: productsAreFetching,
      list: products,
    },
    variants: {
      isFetching: variantsAreFetching,
      list: variants,
    },
    error,
    isFetching,
    noteRow: {
      active: noteRow,
      error: noteRowError,
      isFetching: noteRowIsFetching,
      isExtra: noteRowIsExtra,
    },
    reportDialog: {
      isVisible: reportDialogVisibility,
      suppliers,
      filter: reportFilter,
    },
  },
  purchaseOrderList: {
    orders: {
      filteredList: orders,
    },
  },
  form,
}) => ({
  order,
  rowsById,
  extraRowsById,
  allRowsByRoom,
  additionalRows,
  allAdditionalRowsByRoom,
  customRows,
  rooms,
  additionalRow,
  additionalRowIsFetching,
  additionalRowError,
  linkedAdditionalRow,
  linkedAdditionalRowIsFetching,
  linkedAdditionalRowError,
  products,
  productsAreFetching,
  variants,
  variantsAreFetching,
  error,
  isFetching,
  additionalRowFormIsValid: isValid('productRow')({form}),
  additionalRowFormValues: getFormValues('productRow')({form}),
  linkedAdditionalRowFormIsValid: isValid('linkedAdditionalRow')({form}),
  linkedAdditionalRowFormValues: getFormValues('linkedAdditionalRow')({form}),
  noteRow,
  noteRowError,
  noteRowIsFetching,
  noteRowIsExtra,
  noteRowFormIsValid: isValid('noteRow')({form}),
  noteRowFormValues: getFormValues('noteRow')({form}),
  reportDialogVisibility,
  suppliers,
  reportFilter,
  orders,
});

const mapDispatchToProps = dispatch => ({
  updateOrder: (id, data) =>
    dispatch(updateOrder(id, data)),
  createAdditionalRow: (orderId, data) =>
    dispatch(createAdditionalRow(orderId, data)),
  updateAdditionalRow: (id, data) =>
    dispatch(updateAdditionalRow(id, data)),
  removeAdditionalRow: id =>
    dispatch(removeAdditionalRow(id)),
  setAdditionalProductRow: additionalRow =>
    dispatch(setAdditionalProductRow(additionalRow)),
  setLinkedAdditionalRow: row =>
    dispatch(setLinkedAdditionalRow(row)),
  updateLinkedAdditionalRow: (id, data) =>
    dispatch(updateLinkedAdditionalRow(id, data)),
  removeLinkedAdditionalRow: id =>
    dispatch(removeLinkedAdditionalRow(id)),
  clear: () =>
    dispatch(clear()),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  fetchVariants: id =>
    dispatch(fetchVariants(id)),
  fetchData: id => {
    dispatch(fetchProducts());
    if (id) {
      dispatch(fetchOrderData(id));
    }
  },
  setNoteRow: (row, isExtra) =>
    dispatch(setNoteRow(row, isExtra)),
  updateOrderRow: (id, data) =>
    dispatch(updateOrderRow(id, data)),
  updateExtraRow: (id, data) =>
    dispatch(updateExtraRow(id, data)),
  toggleReportDialog: (show: boolean) =>
    dispatch(toggleReportDialog(show)),
  updateReportFilter: (supplierId: ?number, includePlans: boolean) =>
    dispatch(updateReportFilter(supplierId, includePlans)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderContainer);



// WEBPACK FOOTER //
// ./src/containers/PurchaseOrderContainer/PurchaseOrderContainer.js